import { useState } from 'react'
import { useTranslation } from '../i18n'
import { useApi, apiPost, apiDelete } from '../hooks/useApi'
import type { EffortEntry } from '../types'

export function EffortEntries() {
  const { t } = useTranslation()
  const effort = useApi<EffortEntry[]>('/effort-entries')

  const [hours, setHours] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const entries = effort.data ?? []
  const totalHours = entries.reduce((sum, entry) => sum + entry.hours, 0)

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      const value = parseFloat(hours)
      if (isNaN(value) || value <= 0) {
        throw new Error(t('error.fteMustBePositive'))
      }
      if (!fromDate || !toDate) {
        throw new Error(t('misc.effortHelp'))
      }

      await apiPost<EffortEntry>('/effort-entries', {
        hours: value,
        fromDate: new Date(fromDate).toISOString(),
        toDate: new Date(toDate).toISOString(),
      })

      setHours('')
      setFromDate('')
      setToDate('')
      effort.refetch()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('error.failedToUpdateSettings'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    setError(null)
    try {
      await apiDelete(`/effort-entries/${id}`)
      effort.refetch()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('error.failedToUpdateSettings'))
    }
  }

  return (
    <div className="card" data-testid="effort-entries" style={{ marginTop: '1.5rem' }}>
      <h2 className="card-title">{t('label.effortTracking')}</h2>

      <form
        onSubmit={handleAdd}
        style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.875rem' }}>
          {t('label.hours')}
          <input
            type="number"
            step="0.5"
            min="0.5"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            className="input"
            style={{ width: '100px' }}
            data-testid="effort-hours"
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.875rem' }}>
          {t('label.from')}
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="input"
            data-testid="effort-from"
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.875rem' }}>
          {t('label.to')}
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="input"
            data-testid="effort-to"
          />
        </label>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={saving}
          data-testid="effort-add"
        >
          <i className="pi pi-plus" aria-hidden="true" style={{ marginRight: '0.5rem' }} />
          {saving ? t('misc.saving') : t('button.addEffort')}
        </button>
      </form>

      {error && (
        <p
          style={{ color: 'var(--color-error)', marginTop: '0.5rem', fontSize: '0.875rem' }}
          data-testid="effort-error"
        >
          {error}
        </p>
      )}

      <p
        style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: '0.5rem' }}
      >
        {t('misc.effortHelp')}
      </p>

      {effort.loading ? (
        <div
          style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--color-gray-400)' }}
          data-testid="effort-loading"
        >
          {t('label.loading')}
        </div>
      ) : entries.length === 0 ? (
        <div
          style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--color-gray-400)' }}
          data-testid="no-effort-entries"
        >
          {t('empty.noEffortEntries')}
        </div>
      ) : (
        <div style={{ maxHeight: '500px', overflowY: 'auto', marginTop: '1rem' }}>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} data-testid="effort-list">
            {entries.map((entry) => (
              <li
                key={entry.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0',
                  borderBottom: '1px solid var(--color-border)',
                }}
              >
                <span style={{ fontWeight: 600, width: '90px' }}>
                  {entry.hours} {t('label.hours')}
                </span>
                <span
                  style={{ flex: 1, color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}
                >
                  {new Date(entry.fromDate).toLocaleDateString()} –{' '}
                  {new Date(entry.toDate).toLocaleDateString()}
                </span>
                <button
                  onClick={() => handleDelete(entry.id)}
                  className="btn btn-danger btn-sm"
                  data-testid="effort-delete"
                  aria-label={t('button.delete')}
                  title={t('button.delete')}
                >
                  <i className="pi pi-trash" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
          <p style={{ marginTop: '0.75rem', fontWeight: 600 }} data-testid="effort-total">
            {totalHours} {t('label.hours')}
          </p>
        </div>
      )}
    </div>
  )
}
